import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { notificationsClient } from 'clients/NotificationsClient/NotificationsClient';
import { queryClient } from 'config/queryClient';
import { UserContext } from 'shared/legacy/contexts/UserContext/UserContext';
import { useOauthNylasStatusQuery } from 'shared/legacy/hooks/useOauthNylasStatusQuery';
import { QueryKey } from 'types/query';
import { AccountStatus, UseContainerProps } from './CandidateEmailSettingsContext.types';

export const useContainer = ({ onClosePopup, onNewValidEmailConnection }: UseContainerProps = {}) => {
  const { currentUser } = useContext(UserContext);
  const [currentEmailStatus, setCurrentEmailStatus] = useState(AccountStatus.NonExistent);
  const [currentCalendarStatus, setCurrentCalendarStatus] = useState<AccountStatus>(AccountStatus.NonExistent);

  const [isNewConnection, setIsNewConnection] = useState(false);

  const { data: url } = useQuery([`${currentUser?.email}-oauth-nylas-url`], notificationsClient.connectEmailAccount);

  const oauthUrl = url?.data;

  const openOAuthPopup = useCallback(() => {
    const oauthPopup = window.open(oauthUrl, 'popup', 'popup=true,resizable=1,width=600,height=600');

    const timer = setInterval(async function () {
      if (oauthPopup?.closed) {
        clearInterval(timer);
        setIsNewConnection(true);

        await queryClient.resetQueries([QueryKey.useOauthNylasStatusQuery, currentUser?.email]);

        if (onClosePopup) {
          onClosePopup();
        }
      }
    }, 1000);
  }, [oauthUrl]);

  const { data: { email: emailStatus, calendar: calendarStatus } = {}, isLoading: isLoadingEmailStatus } =
    useOauthNylasStatusQuery();

  useEffect(() => {
    if (emailStatus) {
      const wasEmailValidBefore = currentEmailStatus === AccountStatus.Valid;

      setCurrentEmailStatus(emailStatus);
      calendarStatus && setCurrentCalendarStatus(calendarStatus);

      if (emailStatus === AccountStatus.Valid && isNewConnection && !wasEmailValidBefore) {
        onNewValidEmailConnection?.();
      }

      setIsNewConnection(false);
    }
  }, [emailStatus, isNewConnection]);

  return useMemo(() => {
    return {
      currentEmailStatus,
      isLoadingEmailStatus,
      currentCalendarStatus,
      openOAuthPopup,
    };
  }, [currentEmailStatus, isLoadingEmailStatus, currentCalendarStatus, openOAuthPopup]);
};
